/**
 * Sales person mappings aligned with backend/config/salesFieldLabelMappings.js
 * (KLAVERT / VKOVERT / REGIO_GROUP). Keep in sync when verts/regios change.
 *
 * Multiple V/R codes can map to the same person (e.g. Dirk: V004+V009, Sanny: V015–V019).
 */

const SALES_PEOPLE = {
  NIBR: "Nicolas Bruggeman",
  DIAN: "Dirk Antonis",
  STDE: "Stefan Dekeukelaere",
  JOBO: "Jonas De Bock",
  KODE: "Koen De Bruyne",
  SAD: "Sanny De Baere",
  DIGR: "Dieter Grimmonprez",
  HEGR: "Henk Grootaerd",
  STGE: "Stefaan Gevaert",
};

/** V-codes / ERP vert keys → person name (KLAVERT / VKOVERT) */
const VERT_CODE_TO_PERSON = {
  V003: SALES_PEOPLE.NIBR,
  V004: SALES_PEOPLE.DIAN,
  V006: SALES_PEOPLE.STDE,
  V007: SALES_PEOPLE.JOBO,
  V008: SALES_PEOPLE.KODE,
  V009: SALES_PEOPLE.DIAN,
  V015: SALES_PEOPLE.SAD,
  V016: SALES_PEOPLE.SAD,
  V017: SALES_PEOPLE.SAD,
  V018: SALES_PEOPLE.SAD,
  V019: SALES_PEOPLE.SAD,
  GROOTAERDHENK: SALES_PEOPLE.HEGR,
  GRIMMONPREZDIET: SALES_PEOPLE.DIGR,
  GEVAERTSTEFAAN: SALES_PEOPLE.STGE,
};

/** R-codes → person name (REGIO_GROUP) */
const REGIO_CODE_TO_PERSON = {
  R003: SALES_PEOPLE.NIBR,
  R004: SALES_PEOPLE.DIAN,
  R006: SALES_PEOPLE.STDE,
  R007: SALES_PEOPLE.JOBO,
  R008: SALES_PEOPLE.KODE,
  R009: SALES_PEOPLE.DIAN,
  R015: SALES_PEOPLE.SAD,
  R016: SALES_PEOPLE.SAD,
  R017: SALES_PEOPLE.SAD,
  R018: SALES_PEOPLE.SAD,
  R019: SALES_PEOPLE.SAD,
};

const UNKNOWN_PERSON = "Onbekend";

const normalizeCode = (value) =>
  String(value == null ? "" : value)
    .trim()
    .toUpperCase();

const formatRegionCode = (prefix, regionNumber) =>
  prefix + ("000" + Number(regionNumber)).slice(-3);

/**
 * Resolve display person for a subscription from vert and/or regio codes.
 * Prefers vert (KLAVERT), falls back to regio (REGIO_GROUP).
 */
const resolveSalesPersonFromVertOrRegio = (vert, regio) => {
  const vertKey = normalizeCode(vert);
  if (vertKey && VERT_CODE_TO_PERSON[vertKey]) {
    return VERT_CODE_TO_PERSON[vertKey];
  }
  const regioKey = normalizeCode(regio);
  if (regioKey && REGIO_CODE_TO_PERSON[regioKey]) {
    return REGIO_CODE_TO_PERSON[regioKey];
  }
  return UNKNOWN_PERSON;
};

/**
 * user.regio numbers (e.g. [4, 9] or [15]) → unique person names,
 * covering every V/R code those numbers map to.
 */
const resolveSalesPeopleFromRegioNumbers = (regioNumbers = []) => {
  if (!Array.isArray(regioNumbers) || regioNumbers.length === 0) return [];
  const names = new Set();
  regioNumbers.forEach((n) => {
    const vertCode = formatRegionCode("V", n);
    const regioCode = formatRegionCode("R", n);
    const fromVert = VERT_CODE_TO_PERSON[vertCode];
    const fromRegio = REGIO_CODE_TO_PERSON[regioCode];
    if (fromVert) names.add(fromVert);
    if (fromRegio) names.add(fromRegio);
  });
  return [...names];
};

/** Unique person names in stable display order (for tag tiles). */
const getUniqueSalesPersonNames = () => [...new Set(Object.values(SALES_PEOPLE))];

const PRESENCE_TAGS = {
  AANWEZIG: {
    show: true,
    alwaysShow: true,
    color: "success",
    icon: "fa-user-check",
    text: "Aanwezig",
    sort: 10,
  },
  AFWEZIG: {
    show: true,
    alwaysShow: true,
    color: "secondary",
    icon: "fa-user-xmark",
    text: "Afwezig",
    sort: 20,
  },
};

/**
 * TagFilterWrapper config: aanwezig/afwezig + one tile per sales person.
 */
const buildEventAanwezigheidTagConfig = () => {
  const tags = { ...PRESENCE_TAGS };
  getUniqueSalesPersonNames().forEach((name, index) => {
    tags[name] = {
      show: true,
      color: "info",
      icon: "fa-user-tie",
      text: name,
      sort: 30 + index,
    };
  });
  tags[UNKNOWN_PERSON] = {
    show: true,
    color: "warning",
    icon: "fa-circle-question",
    text: UNKNOWN_PERSON,
    sort: 90,
  };
  return tags;
};

/**
 * Tags attached to one subscription row for TagFilterWrapper.
 */
const buildSubscriptionTags = (subscription = {}) => {
  const present = Boolean(subscription.scannedAt);
  const person = resolveSalesPersonFromVertOrRegio(
    subscription.vert,
    subscription.regio
  );
  return [present ? "AANWEZIG" : "AFWEZIG", person];
};

module.exports = {
  SALES_PEOPLE,
  VERT_CODE_TO_PERSON,
  REGIO_CODE_TO_PERSON,
  UNKNOWN_PERSON,
  resolveSalesPersonFromVertOrRegio,
  resolveSalesPeopleFromRegioNumbers,
  getUniqueSalesPersonNames,
  buildEventAanwezigheidTagConfig,
  buildSubscriptionTags,
};
